import OnboardingLayout from "layout/OnboardingLayout";
import {
  App,
  AutoComplete,
  Button,
  Divider,
  Empty,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Select,
  Typography,
} from "antd";
import { ApiService } from "api/api.service";
import { SingleQueryWrapper } from "components/QueryWrapper";
import { Fragment, useState } from "react";
import { TbChevronLeft, TbChevronRight, TbMinus, TbPlus } from "react-icons/tb";
import { useNavigate } from "react-router-dom";
import { APPLICATION_STATUS, BusinessInfo, ClientDetails, SelectedBank } from "types/application.types";
import { stateNames } from "utils/constants";
import { getErrorMessage, getPdfTypeFromPlan } from "utils/functions";
import { storedTokens } from "utils/storedTokens";
import ApplicationRedirect from "./components/ApplicationRedirect";
import { ONBOARDING_ROUTES } from "utils/routes";
import PageTitle from "components/PageTitle";
import DashboardLayout from "layout/DashboardLayout";

// enum CLIENT_STATUS {
//   NOT_PAYING = "Not paying",
//   SETTLEMENT = "Something needs to settle",
//   BANKRUPT = "Bankruptcy",
//   CUST = "Customer",
// }

type T_PersonalInfoForm = {
  client_details: ClientDetails;
  business_info: BusinessInfo;
  selected_bank: SelectedBank;
};

const autoCompleteOption: { label: string; value: string }[] = stateNames.map((state) => {
  return { label: state, value: state };
});

const PersonalInfo = () => {
  const navigate = useNavigate();
  const applicationId = storedTokens.selectedApplicationId.getToken();
  const applicationDetails = ApiService.onboarding.useGetApplicationById(applicationId);
  const updateApplication = ApiService.onboarding.useUpdateApplication(applicationId);
  const updateStatus = ApiService.onboarding.useUpdateStatus(applicationId);
  const docSignPrep = ApiService.onboarding.useStartSignDocPrep();

  const [form] = Form.useForm<T_PersonalInfoForm>();
  const [options, setOptions] = useState<{ label: string; value: string }[] | undefined>(autoCompleteOption);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [previewContract, setPreviewContract] = useState(false);
  const [typeSaving, setTypeSaving] = useState<"draft" | "send">();

  if (!applicationId) {
    return <ApplicationRedirect />;
  }

  const handleFormSubmission = (values: T_PersonalInfoForm) => {
    setTypeSaving("send");

    updateApplication.mutate(values, {
      onSuccess: async () => {
        try {
          await docSignPrep.mutateAsync({ applicationId: applicationId });
          updateStatus.mutateAsync({ status: APPLICATION_STATUS.PERSONAL_INFO });
          setTypeSaving(undefined);
          navigate(ONBOARDING_ROUTES.SignContract);
        } catch (error) {
          setTypeSaving(undefined);
          message.error(getErrorMessage(error));
        }
      },
      onError: () => {
        setTypeSaving(undefined);
      },
    });
  };

  const handleDraftSave = () => {
    setTypeSaving("draft");

    updateApplication.mutate(form.getFieldsValue(), {
      onSuccess: async () => {
        updateStatus.mutate(
          { status: APPLICATION_STATUS.PERSONAL_INFO },
          {
            onSuccess: () => {
              message.success("Saved as draft");
              setTypeSaving(undefined);
              setIsConfirmModalOpen(false);
            },
            onError: () => {
              setTypeSaving(undefined);
            },
          },
        );
      },
      onError: () => {
        setTypeSaving(undefined);
      },
    });
  };

  return (
    <DashboardLayout>
      <OnboardingLayout>
        <PageTitle pageTitle={[]} title="Personal Info" />
        <SingleQueryWrapper
          showLoaderOn="isFetching"
          query={applicationDetails}
          renderData={(currApplication) => {
            const plan = currApplication.result.strategy?.plan;
            let showPreview = false;
            let query = "";
            if (plan && plan.length) {
              showPreview = true;
              const q = getPdfTypeFromPlan(plan);
              if (q !== null) query = q;
            }

            return (
              <Form
                form={form}
                layout="vertical"
                scrollToFirstError
                initialValues={currApplication}
                onFinish={handleFormSubmission}
              >
                <div className="flex flex-col gap-8 max-w-[900px] mx-auto p-5">
                  <div className="bg-white border border-solid border-gray-300 rounded-3xl overflow-auto">
                    <div className="flex justify-between items-center px-6 py-4 bg-gray-100 border-[0] border-b border-solid border-gray-300 ">
                      <Typography.Title level={4} className="!m-0">
                        Customer Details
                      </Typography.Title>
                    </div>
                    <div className="p-5">
                      <div className="grid grid-cols-12 gap-2">
                        <div className="col-span-12">
                          <Form.Item<T_PersonalInfoForm>
                            name={["client_details", "name"]}
                            label="Full Name"
                            rules={[{ required: true }]}
                          >
                            <Input />
                          </Form.Item>
                        </div>
                      </div>

                      <div className="grid grid-cols-12 gap-2">
                        <div className="col-span-6">
                          <Form.Item<T_PersonalInfoForm>
                            name={["client_details", "ssn"]}
                            label="SSN"
                            rules={[
                              { required: true, pattern: new RegExp(/^\d+$/), message: "Only digits are allowed" },
                              { len: 9, message: "SSN must be 9 digits" },
                            ]}
                          >
                            <Input maxLength={9} minLength={9} />
                          </Form.Item>
                        </div>
                        <div className="col-span-6">
                          <Form.Item<T_PersonalInfoForm>
                            name={["client_details", "phone"]}
                            label="Mobile No."
                            rules={[{ required: true }]}
                          >
                            <InputNumber<string>
                              type="tel"
                              minLength={14}
                              maxLength={14}
                              style={{ width: "100%" }}
                              formatter={(value) => {
                                if (!value) return "";
                                const onlyNumbers = `${value}`.replace(/\D/g, "");
                                const formatted = onlyNumbers.replace(
                                  /^(\d{0,3})(\d{0,3})(\d{0,4})$/,
                                  (match, p1, p2, p3) =>
                                    `${p1 ? `(${p1}) ` : ""}${p2 ? `${p2}` : ""}${p3 ? `-${p3}` : ""}`,
                                );
                                return formatted.trim();
                              }}
                              parser={(value) => value?.replace(/\D/g, "") as unknown as string}
                              controls={false}
                            />
                          </Form.Item>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col gap-8 max-w-[900px] mx-auto p-5">
                  <div className="bg-white border border-solid border-gray-300 rounded-3xl overflow-auto">
                    <div className="flex justify-between items-center px-6 py-4 bg-gray-100 border-[0] border-b border-solid border-gray-300 ">
                      <Typography.Title level={4} className="!m-0">
                        Business Information
                      </Typography.Title>
                    </div>
                    <div className="p-5">
                      <Form.Item<T_PersonalInfoForm>
                        name={["business_info", "company_name"]}
                        label="Company Name"
                        rules={[{ required: true }]}
                      >
                        <Input />
                      </Form.Item>
                      <div className="grid grid-cols-12 gap-2">
                        <div className="col-span-6">
                          <Form.Item<T_PersonalInfoForm>
                            name={["business_info", "email"]}
                            label="Email"
                            rules={[{ required: true, type: "email" }]}
                          >
                            <Input inputMode="email" />
                          </Form.Item>
                        </div>
                        <div className="col-span-6">
                          <Form.Item<T_PersonalInfoForm>
                            name={["business_info", "phone"]}
                            label="Phone"
                            rules={[{ required: true }]}
                          >
                            <InputNumber<string>
                              type="tel"
                              minLength={14}
                              maxLength={14}
                              style={{ width: "100%" }}
                              formatter={(value) => {
                                if (!value) return "";
                                const onlyNumbers = `${value}`.replace(/\D/g, "");
                                const formatted = onlyNumbers.replace(
                                  /^(\d{0,3})(\d{0,3})(\d{0,4})$/,
                                  (match, p1, p2, p3) =>
                                    `${p1 ? `(${p1}) ` : ""}${p2 ? `${p2}` : ""}${p3 ? `-${p3}` : ""}`,
                                );
                                return formatted.trim();
                              }}
                              parser={(value) => value?.replace(/\D/g, "") as unknown as string}
                              controls={false}
                            />
                          </Form.Item>
                        </div>
                      </div>
                      <div className="grid grid-cols-12 gap-2">
                        <div className="col-span-6">
                          <Form.Item<T_PersonalInfoForm>
                            name={["business_info", "business_type"]}
                            label="Business Type"
                            rules={[{ required: true }]}
                          >
                            <Select
                              options={[
                                "Corporation",
                                "Limited Liability Company",
                                "Partnership",
                                "Limited Partnership",
                                "Limited Liability Partnership",
                                "Sole Proprietorship",
                                "Other",
                              ].map((el) => ({ label: el, value: el }))}
                            />
                          </Form.Item>
                        </div>
                        <div className="col-span-6">
                          <Form.Item<T_PersonalInfoForm>
                            name={["business_info", "ein_number"]}
                            label="EIN Number"
                            rules={[
                              { required: true, pattern: new RegExp(/^\d+$/), message: "Only digits are allowed" },
                              { len: 9, message: "EIN must be 9 digits" },
                            ]}
                          >
                            <Input maxLength={9} minLength={9} />
                          </Form.Item>
                        </div>
                        {/* <div className="col-span-4">
                          <Form.Item<T_PersonalInfoForm>
                            name={"business_type"}
                            label="Type of Business"
                            rules={[{ required: true }]}
                          >
                            <Input />
                          </Form.Item>
                        </div> */}
                      </div>

                      {/* <Form.Item<T_PersonalInfoForm> name={"description"} label="Description">
                        <Input />
                      </Form.Item> */}
                      <Divider></Divider>
                      <Form.Item<T_PersonalInfoForm>
                        name={["business_info", "street_address"]}
                        label="Street Address"
                        rules={[{ required: true }]}
                      >
                        <Input />
                      </Form.Item>
                      <div className="grid grid-cols-12 gap-2">
                        <div className="col-span-4">
                          <Form.Item<T_PersonalInfoForm>
                            name={["business_info", "city"]}
                            label="City"
                            rules={[{ required: true }]}
                          >
                            <Input />
                          </Form.Item>
                        </div>
                        <div className="col-span-4">
                          <Form.Item<T_PersonalInfoForm>
                            name={["business_info", "state"]}
                            label="State"
                            rules={[{ required: true }]}
                          >
                            <AutoComplete
                              options={options}
                              style={{ width: "100%" }}
                              onBlur={() => setOptions(autoCompleteOption)}
                              onFocus={() => setOptions(autoCompleteOption)}
                              // onSelect={onSelect}
                              onSearch={(text) => {
                                setOptions(
                                  autoCompleteOption.filter((option) => option.value.toLowerCase().startsWith(text)),
                                );
                              }}
                              placeholder="Search state here"
                            />
                          </Form.Item>
                        </div>

                        <div className="col-span-4">
                          <Form.Item<T_PersonalInfoForm>
                            name={["business_info", "zip_code"]}
                            label="ZIP Code"
                            rules={[{ required: true }]}
                          >
                            <Input minLength={5} maxLength={5} />
                          </Form.Item>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col gap-8 max-w-[900px] mx-auto p-5">
                  <div className="bg-white border border-solid border-gray-300 rounded-3xl overflow-auto">
                    <Form.List name={["client_details", "cosigners"]}>
                      {(fields, { add, remove }) => (
                        <>
                          <div className="flex justify-between items-center px-6 py-4 bg-gray-100 border-[0] border-b border-solid border-gray-300 ">
                            <Typography.Title level={4} className="!m-0">
                              Cosigner Details
                            </Typography.Title>
                            {fields.length === 0 ? (
                              <Button onClick={() => add()} icon={<TbPlus />}>
                                Add Cosigner
                              </Button>
                            ) : (
                              <Button onClick={() => remove(0)} icon={<TbMinus />}>
                                Remove Cosigner
                              </Button>
                            )}
                          </div>
                          <div className="p-5">
                            {fields.length === 0 ? (
                              <Empty description="Don't have cosigner" image={Empty.PRESENTED_IMAGE_DEFAULT} />
                            ) : null}
                            {fields.map(({ key, name, ...restField }) => (
                              <Fragment key={key}>
                                <Form.Item
                                  {...restField}
                                  label="Cosigner Name"
                                  name={[name, "name"]}
                                  rules={[{ required: true, message: "Missing cosigner name" }]}
                                >
                                  <Input placeholder="Name" />
                                </Form.Item>
                                <Form.Item
                                  {...restField}
                                  label="Cosigner Email"
                                  name={[name, "email"]}
                                  rules={[
                                    { required: true, message: "Missing cosigner email" },
                                    { type: "email", message: "Invalid email" },
                                  ]}
                                >
                                  <Input placeholder="Email" />
                                </Form.Item>
                              </Fragment>
                            ))}
                          </div>
                        </>
                      )}
                    </Form.List>
                  </div>
                </div>

                <div className="flex flex-col gap-8 max-w-[900px] mx-auto p-5 mb-[98px]">
                  <div className="bg-white border border-solid border-gray-300 rounded-3xl overflow-auto">
                    <div className="flex justify-between items-center px-6 py-4 bg-gray-100 border-[0] border-b border-solid border-gray-300 ">
                      <Typography.Title level={4} className="!m-0">
                        Payment Information
                      </Typography.Title>
                    </div>
                    <div className="p-5">
                      <Form.Item<T_PersonalInfoForm>
                        name={["selected_bank", "bank_name"]}
                        label="Bank Name"
                        rules={[{ required: true }]}
                      >
                        <Input style={{ width: "100%" }} />
                      </Form.Item>
                      <div className="grid grid-cols-12 gap-2">
                        <div className="col-span-6">
                          <Form.Item<T_PersonalInfoForm>
                            name={["selected_bank", "name_on_account"]}
                            label="Name on account"
                            rules={[{ required: true }]}
                          >
                            <Input />
                          </Form.Item>
                        </div>
                        <div className="col-span-6">
                          <Form.Item<T_PersonalInfoForm>
                            name={["selected_bank", "routing_no"]}
                            label="Routing No"
                            rules={[{ required: true }]}
                          >
                            <Input maxLength={9} minLength={9} />
                          </Form.Item>
                        </div>
                      </div>
                      <Form.Item<T_PersonalInfoForm>
                        name={["selected_bank", "account_no"]}
                        label="Account No"
                        rules={[{ required: true }]}
                      >
                        <Input style={{ width: "100%" }} />
                      </Form.Item>
                    </div>
                  </div>
                </div>
                <Modal
                  footer={null}
                  open={isConfirmModalOpen}
                  onCancel={() => setIsConfirmModalOpen(false)}
                  title="Confirm you want to send contract to the client?"
                >
                  <div className="flex gap-3 mt-3">
                    <Button
                      className="mr-auto"
                      onClick={() => {
                        setIsConfirmModalOpen(false);
                      }}
                    >
                      Cancel
                    </Button>
                    {showPreview && query && (
                      <Button
                        variant="filled"
                        color="primary"
                        className="border border-solid border-purple-300"
                        // loading={typeSaving === "draft"}
                        onClick={() => {
                          setPreviewContract(true);
                        }}
                      >
                        Preview
                      </Button>
                    )}
                    <Button
                      variant="filled"
                      color="primary"
                      className="border border-solid border-purple-300"
                      loading={typeSaving === "draft"}
                      onClick={() => {
                        handleDraftSave();
                      }}
                    >
                      No, save as draft
                    </Button>
                    <Button
                      type="primary"
                      loading={typeSaving === "send"}
                      onClick={() => {
                        form.submit();
                      }}
                    >
                      Yes, send
                    </Button>
                  </div>
                </Modal>
                <Modal
                  footer={null}
                  open={previewContract}
                  onCancel={() => setPreviewContract(false)}
                  title="Contract Preview"
                  width={800}
                >
                  <iframe
                    src={`${String(import.meta.env.VITE_APP_API_URL).split("api/v1")[0]}document-pdf/${currApplication.result.app_key}?type=${query}`}
                    className="w-full outline-none border-none"
                    style={{ height: "calc(100vh - 320px)" }}
                  />
                </Modal>
              </Form>
            );
          }}
        />
        <div className="bg-white fixed bottom-0 left-0 z-50 w-full flex justify-between border border-t border-solid border-gray-300 py-3 px-5">
          <Button
            size="large"
            icon={<TbChevronLeft />}
            onClick={() => {
              navigate(ONBOARDING_ROUTES.ChoosePlan);
            }}
          >
            Back
          </Button>
          <Button
            onClick={() => {
              setIsConfirmModalOpen(true);
            }}
            loading={updateApplication.isPending || updateStatus.isPending || docSignPrep.isPending}
            size="large"
            type="primary"
            icon={<TbChevronRight />}
            iconPosition="end"
            className="w-fit"
          >
            Generate Contract
          </Button>
        </div>
      </OnboardingLayout>
    </DashboardLayout>
  );
};

export default PersonalInfo;
